import { DeleteOutlined } from '@ant-design/icons';
import { Col, Form, Row, Tag } from 'antd';
import React, { useState } from 'react';
import { ClientFeatureConfig, JSONTransform } from '../../../types/Feature';
import JSONTransformEnableSwitch from './JSONTransformEnableSwitch';
import JavascriptCodeInput from '../../Shared/Inputs/JavascriptCodeInput';
import { JSONTransformPathInput, JSONTransformSelect } from './JSONTransformPathInputs';

interface JsonTransformItemProps {
    isDetailMode: boolean | undefined;
    fieldName: number;
    enableControls: boolean;
    jsonTransformConfig: ClientFeatureConfig<JSONTransform> | undefined;
    fieldKey: number | string;
    remove?: (idx: number | number[]) => void;
}

const getLabel = (value: string | undefined, options?: { value: string; label: string }[]): string =>
    options?.find((o) => o.value === value)?.label || value || '';

const JsonTransformItem: React.FC<JsonTransformItemProps> = ({
    isDetailMode,
    jsonTransformConfig,
    enableControls,
    fieldName,
    fieldKey,
    remove,
}: JsonTransformItemProps) => {
    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(!jsonTransformConfig?.config.enabled);
    const httpMethodOptions = [
        { value: 'POST', label: 'POST' },
        { value: 'PUT', label: 'PUT' },
        { value: 'DELETE', label: 'DELETE' },
        { value: 'GET', label: 'GET' },
        { value: 'PATCH', label: 'PATCH' },
        { value: 'any', label: 'Any HTTP method' },
    ];
    const urlMatchingOptions = [
        { value: 'contains', label: 'URL contains' },
        { value: 'startsWith', label: 'URL starts with' },
        { value: 'endsWith', label: 'URL ends with' },
        { value: 'exact', label: 'URL is exactly' },
    ];
    const roConfig = [
        getLabel(jsonTransformConfig?.config.method || 'any', httpMethodOptions),
        jsonTransformConfig?.config.target,
        'where',
        getLabel(jsonTransformConfig?.config.path?.matching || 'contains', urlMatchingOptions),
        `"${jsonTransformConfig?.config.path?.value}"`,
    ].join(' ');

    return (
        <div style={{ borderBottom: 'dashed 1px rgba(0, 0, 0, 0.3)', marginBottom: '40px', paddingTop: '10px' }}>
            <Row>
                <Col span={24} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }}>
                    <Form.Item hidden name={[fieldName, 'featureId']} />
                    <Form.Item hidden name={[fieldName, 'id']} />
                    <Row>
                        {isDetailMode && (
                            <Col span={15}>
                                <b>{roConfig}</b>
                            </Col>
                        )}
                        {!isDetailMode && (
                            <Col md={{ span: 7 }}>
                                <JSONTransformSelect
                                    value={String(jsonTransformConfig?.config.method || 'any')}
                                    fieldName={[fieldName, 'jsonTransformHTTPMethod']}
                                    isDetailMode={isDetailMode}
                                    options={httpMethodOptions}
                                />
                            </Col>
                        )}
                        {!isDetailMode && (
                            <Col md={{ span: 7, offset: 1 }}>
                                <JSONTransformSelect
                                    value={String(jsonTransformConfig?.config.target || 'request')}
                                    fieldName={[fieldName, 'jsonTransformTarget']}
                                    isDetailMode={isDetailMode}
                                    options={[
                                        { value: 'request', label: 'Request' },
                                        { value: 'response', label: 'Response' },
                                    ]}
                                />
                            </Col>
                        )}
                        {isDetailMode && <Col md={{ span: 1, offset: 1 }} />}
                        <Col md={{ span: 5, offset: 2 }}>
                            <JSONTransformEnableSwitch
                                isFeatureDisabled={isFeatureDisabled}
                                setIsFeatureDisabled={setIsFeatureDisabled}
                                fieldName={fieldName}
                                fieldKey={fieldKey}
                                isDetailMode={isDetailMode}
                            />
                        </Col>
                        {!isDetailMode && (
                            <Col md={{ span: 1, offset: 1 }}>
                                <span
                                    className="ant-form-text text-secondary"
                                    style={{ marginTop: '5px', cursor: 'pointer' }}
                                >
                                    <Tag
                                        color="red"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(): void => remove?.(fieldName)}
                                        id={`jsonTransformFunction_delete_${fieldName}`}
                                    >
                                        <DeleteOutlined />
                                    </Tag>
                                </span>
                            </Col>
                        )}
                    </Row>
                    {!isDetailMode && (
                        <Row>
                            <Col md={{ span: 8 }}>
                                <JSONTransformSelect
                                    value={String(jsonTransformConfig?.config.path?.matching || 'contains')}
                                    fieldName={[fieldName, 'jsonPathMatchingMethod']}
                                    isDetailMode={isDetailMode}
                                    options={urlMatchingOptions}
                                />
                            </Col>
                            <Col md={{ span: 15, offset: 1 }}>
                                <JSONTransformPathInput
                                    pathValue={jsonTransformConfig?.config.path?.value}
                                    isDetailMode={isDetailMode}
                                    fieldName={fieldName}
                                />
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
            <Row>
                <Col span={24} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }}>
                    <JavascriptCodeInput
                        isFeatureDisabled={isFeatureDisabled}
                        isDetailMode={isDetailMode}
                        jsCode={jsonTransformConfig?.config.transformFunction}
                        fieldKey={[fieldKey, 'jsonTransformFunction']}
                        fieldName={[fieldName, 'jsonTransformFunction']}
                        enableControls={enableControls}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default JsonTransformItem;
